import React from 'react'
import {
  Anchor,
  Button,
  ConfirmDialog,
  Div,
  Flex,
  IconLink,
  PhoneInput,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import {
  getMarketingContent,
  getPhoneNumberCountries,
  PhoneNumberInput,
} from 'src/graphql'
import {
  useActions,
  usePersistedUserData,
  useQueries,
  useState,
} from 'src/hooks'
import { getFormattedNumber } from './PhoneNumberVerification'
import {
  createPhoneNumberInput,
  getValidatedNumber,
} from 'src/helpers/createPhoneNumberInput'

const PhoneNumberEntry = (props: {
  requestVerificationCode: (phoneNumber: PhoneNumberInput) => void
  onVerificationRequested: (phoneNumber: PhoneNumberInput) => void
  phoneNumber?: PhoneNumberInput
  failureMessage?: string
}) => {
  const { requestVerificationCode, failureMessage } = props
  const { account } = usePersistedUserData()
  const [isDialogOpen, setDialogIsOpen] = useState(false)

  const [
    phoneNumberCountries,
    { complianceMessage, whyMyPhoneNumberMessage },
  ] = useQueries(getPhoneNumberCountries(), getMarketingContent())

  const [selectedCountryIndex, setSelectedCountryIndex] = useState(
    phoneNumberCountries.findIndex(
      country =>
        country.isoCode === (props.phoneNumber?.countryIsoCode ?? 'US'),
    ),
  )
  const [phoneNumber, setPhoneNumber] = useState(
    props.phoneNumber?.nationalNumber ?? '',
  )

  const selectedCountry = phoneNumberCountries[selectedCountryIndex]
  const selectedCountryCode = selectedCountry.isoCode
  const formattedNumber = getFormattedNumber(selectedCountryCode, phoneNumber)
  const validatedNumber = getValidatedNumber(selectedCountryCode, phoneNumber)
  const actions = useActions()

  const onSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault()

    if (!validatedNumber) {
      return
    }

    const phoneNumberInput = createPhoneNumberInput(
      phoneNumber,
      selectedCountry,
    )

    requestVerificationCode(phoneNumberInput)
  }
  return (
    <form onSubmit={onSubmit} autoComplete="on">
      <Flex flexDirection="column">
        <Text type="title" weight="bold">
          {account
            ? "Let's verify your phone number"
            : 'Welcome to SendOutCards!'}
        </Text>
        <Spacer space="x2" />
        <Text type="caption">
          {account &&
          !account.isTrialAffiliate &&
          !account.isQualifiedForFirstFreeCard
            ? 'Verify your phone number so you can use it to log in in the future. Enter your number below to receive a text message with a verification code.'
            : 'We’re so glad you’re here! Enter your phone number below and we will send you a code that you can use to send a free card to anyone in the world.'}
        </Text>
        <Spacer space="x4" />
        <div style={{ textAlign: 'right' }}>
          <span
            style={{
              fontSize: '12px',
              color: 'silver',
            }}
          >
            {complianceMessage.content}
          </span>
          <PhoneInput
            countries={phoneNumberCountries}
            selectedCountryIndex={selectedCountryIndex}
            onSelectedCountryIndexChange={setSelectedCountryIndex}
            phoneNumber={formattedNumber}
            onPhoneNumberChange={setPhoneNumber}
            maxHeight="125px"
            message={
              failureMessage
                ? {
                    type: 'danger',
                    content: failureMessage,
                  }
                : undefined
            }
          />
        </div>
        <Spacer space="x2" />
        <Div>
          <IconLink
            icon={'information'}
            title={'Why my phone number?'}
            fill="primaryBodyText"
            onClick={() => setDialogIsOpen(true)}
            size="small"
          />
        </Div>
        <Spacer space="x9" />
        <Flex flexDirection="column" alignItems="center">
          <Div width={'230px'} id="enter_phone_number_btn">
            <Button
              fullWidth={true}
              title={`${!formattedNumber ? 'Enter Phone Number' : 'Send Code'}`}
              onClick="submitForm"
              hover={true}
              disabled={phoneNumber === ''}
            />
          </Div>
          <Spacer space="x2" />
          {!account && (
            <Anchor
              size={'small'}
              isDecorated={true}
              onClick={() => actions.closePhoneNumberVerification()}
            >
              No thanks, I'll pay for my card
            </Anchor>
          )}
        </Flex>
      </Flex>
      <ConfirmDialog
        isOpen={isDialogOpen}
        title={whyMyPhoneNumberMessage.title}
        description={whyMyPhoneNumberMessage.content}
        accept={{ title: 'Got it', onClick: () => setDialogIsOpen(false) }}
        primaryAction={'accept'}
        onClose={() => setDialogIsOpen(false)}
      />
    </form>
  )
}

export default PhoneNumberEntry
